import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { Box, Flex, Text, Stack, SimpleGrid, Spinner, Link } from "@chakra-ui/react";
import { Inbox, Flag, BookOpen, ClipboardCheck } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { cachePut, cacheGet, safeQuery, safeAll } from "@/lib/offline";
import { isUrgent, urgentOrFilter } from "@/lib/urgent";
import { SafetyBulletinsCard } from "@/components/SafetyBulletinsCard";
import { AssignedTasks } from "@/pages/Tasks";
import { NeedsAttention } from "@/components/NeedsAttention";
import { Greeting, HomeCard, QuickLink, StatTile, EmptyState, RecentSubs } from "@/pages/home/shared";
import { ScheduleFormButton } from "@/components/ScheduleFormButton";
import { colors } from "@/theme/colors";

const ACCENT_AMBER = { bg: "#FBEEDA", fg: "#8A5106" };
const ACCENT_POPPY = { bg: colors.poppyTint, fg: colors.poppy };
const ACCENT_TEAL = { bg: colors.tealTint, fg: colors.teal };
const ACCENT_SAGE = { bg: "#EAF0E7", fg: colors.sageDeep };

export default function SupervisorHome() {
  const { user, profile, companyName, hasFeature, activeCompanyId } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [unknown, setUnknown] = useState(false);
  const [urgentCount, setUrgentCount] = useState(null);

  const load = useCallback(async () => {
    const [{ data, error }, cntRes] = await safeAll([
      supabase.from("submissions")
        .select("id,template_title,template_type,worker_name,location_name,status,flagged,revision,created_at")
        .eq("company_id", activeCompanyId)
        .order("created_at", { ascending: false }).limit(300),
      // Server-side count for the "Needs review" tile — the 300-row fetch above
      // truncates on large tenants and would under-count (RLS keeps it crew-scoped).
      supabase.from("submissions").select("id", { count: "exact", head: true })
        .eq("company_id", activeCompanyId).eq("status", "pending").or(urgentOrFilter()),
    ]);
    const cnt = cntRes.error ? null : cntRes.count;
    if (error) {
      // Load failed: cached data (even a real zero) is real data; unknown only when nothing was ever loaded.
      const cached = cacheGet(user.id, "home_subs");
      setSubs(cached ? cached.subs || [] : []);
      setUrgentCount(cached ? cached.urgentCount ?? null : null);
      setUnknown(!cached);
    } else {
      setSubs(data || []);
      setUrgentCount(cnt);
      setUnknown(false);
      cachePut(user.id, "home_subs", { subs: data || [], urgentCount: cnt });
    }
    setLoading(false);
  }, [user.id, activeCompanyId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Flex minH="40vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>;

  const active = subs.filter((s) => s.status !== "superseded");
  const pending = active.filter((s) => s.status === "pending");
  const flagged = pending.filter((s) => s.flagged);
  const recent = active.slice(0, 6);

  return (
    <Box data-testid="home-supervisor" maxW="900px">
      <Flex justify="space-between" align="flex-start" gap="3" flexWrap="wrap">
        <Greeting name={profile?.full_name} sub={`Here's what your crews at ${companyName || "your company"} need from you.`} />
        <ScheduleFormButton />
      </Flex>

      <Box mb="6"><AssignedTasks compact /></Box>

      <NeedsAttention />

      <Text fontWeight="700" fontSize="sm" color={colors.textPrimary} mb="2.5">Needs your attention</Text>
      <SimpleGrid columns={{ base: 1, sm: 2 }} gap="4" mb="6">
        <StatTile icon={ClipboardCheck} label="Needs review" value={urgentCount ?? pending.filter(isUrgent).length}
          sub="tap to review & resolve" accent={ACCENT_AMBER} unavailable={unknown}
          to="/submissions?status=pending" testId="home-stat-pending" />
        <StatTile icon={Flag} label="Flagged submissions" value={flagged.length}
          sub="Fail / Critical answers awaiting review" accent={ACCENT_POPPY} unavailable={unknown}
          to="/submissions?status=pending" testId="home-stat-flagged" />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 3 }} gap="4">
        <Box gridColumn={{ base: "auto", lg: "span 2" }}>
          <HomeCard title="Recent activity" testId="home-recent"
            action={<Link as={RouterLink} to="/submissions" fontSize="xs" color={colors.tealHover} fontWeight="600" data-testid="home-view-all-subs">View all</Link>}>
            {recent.length === 0 ? (
              <EmptyState>{unknown ? "You're offline — recent activity will appear once this device has loaded it." : "No submissions yet — your crews' forms will appear here."}</EmptyState>
            ) : (
              <RecentSubs subs={recent} onOpen={(id) => navigate(`/submissions/${id}`)} />
            )}
          </HomeCard>
        </Box>
        <Stack gap="3">
          <SafetyBulletinsCard />
          <QuickLink icon={Inbox} label="Submissions" desc="Review, sign off, export" to="/submissions" testId="home-link-submissions" />
          {hasFeature("safety_bulletins") && <QuickLink icon={BookOpen} label="Resources" desc="Manuals & safety bulletins" to="/resources" testId="home-link-resources" />}
        </Stack>
      </SimpleGrid>
    </Box>
  );
}
